import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import { useTranslation } from "../../../hooks/useTranslation";

type Props = {
  open: boolean;
  onClose: () => void;
  asin: string;
  domain?: string;
};

export const KeepaGraphDialog = ({
  open,
  onClose,
  asin,
  domain = "com",
}: Props) => {
  const { translate } = useTranslation(
    "pages.product.sidebar.keepaGraphDialog",
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [range, setRange] = useState(90);

  const url = `https://graph.keepa.com/?asin=${asin.toUpperCase()}&domain=${domain}&range=${range}&width=900&height=350&bb=1&amazon=1&fba=1&fbm=1`;

  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const handleRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: number | null,
  ) => {
    if (value !== null) {
      setLoading(true);
      setError(false);
      setRange(value);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{translate("title")}</DialogTitle>

      <DialogContent sx={{ textAlign: "center", p: 2 }}>
        {/* RANGE SELECTOR */}
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={handleRangeChange}
            size="small"
          >
            <ToggleButton value={7}>{translate("range.week")}</ToggleButton>

            <ToggleButton value={30}>{translate("range.month")}</ToggleButton>

            <ToggleButton value={90}>
              {translate("range.threeMonths")}
            </ToggleButton>

            <ToggleButton value={180}>
              {translate("range.sixMonths")}
            </ToggleButton>

            <ToggleButton value={365}>{translate("range.year")}</ToggleButton>

            <ToggleButton value={9999}>{translate("range.all")}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* GRAPH AREA */}
        <Box
          sx={{
            position: "relative",
            minHeight: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* LOADING */}
          {loading && !error && <CircularProgress />}

          {/* ERROR PLACEHOLDER */}
          {error && (
            <Box sx={{ textAlign: "center", color: "text.secondary" }}>
              <InsertChartOutlinedIcon sx={{ fontSize: 56, mb: 1 }} />

              <Typography variant="subtitle1" fontWeight={600}>
                {translate("error.title")}
              </Typography>

              <Typography variant="body2">
                {translate("error.description")}
              </Typography>
            </Box>
          )}

          {/* IMAGE */}
          {!error && (
            <img
              key={range}
              src={url}
              alt={translate("imageAlt")}
              onLoad={handleLoad}
              onError={handleError}
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: 8,
                display: loading ? "none" : "block",
              }}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
